import React from "react";
import { Alert, Box, Typography } from "@mui/material";
import LabeledField from "../../components/form/LabeledField";
import WorkflowDateField from "./WorkflowDateField";
import WorkflowPifSelectField from "./WorkflowPifSelectField";
import WorkflowPifTextField, { pifControlId } from "./WorkflowPifTextField";
import { PifOption, PifSectionProps, pifDetailsMode } from "./pifDetailsModel";
import { PIF_DETAILS_FIELDS, PIF_EMPLOYMENT_TYPES, PIF_SWITCH_OR_ADDITION } from "./pifWorkflowKeys";
import { isPifStartDatePast } from "../../pages/supervision/PostInterviewForm/pifValidation";

const F = PIF_DETAILS_FIELDS;

// Decision 3.2's wording. No default: an existing employee must choose (the placeholder is empty).
const SWITCH_OR_ADDITION: PifOption[] = [
  { id: PIF_SWITCH_OR_ADDITION.switch, text: "Position change (replaces current one)" },
  { id: PIF_SWITCH_OR_ADDITION.addition, text: "Additional position (keeps current one)" },
];

/**
 * pif-3 Position. Department gates Position (disabled until one is chosen, then that department's
 * positions only); Position sets the band, the driving questions, Community DSP and full time.
 */
const WorkflowPifPositionSection: React.FC<PifSectionProps> = ({ identity, values, errors, reference, onChange }) => {
  const field = { values, errors, onChange };
  const department = values[F.department] ?? "";
  const firstWorkingDay = values[F.firstWorkingDay] ?? "";
  const employmentTypes: PifOption[] = [
    { id: PIF_EMPLOYMENT_TYPES.partTime, text: "Part-time" },
    ...(reference.fullTimeAllowed ? [{ id: PIF_EMPLOYMENT_TYPES.fullTime, text: "Full-time" }] : []),
  ];

  return (
    <>
      <Typography component="h3" className="workflow-run__section-title">
        Position
      </Typography>
      <Box className="workflow-run__grid">
        {pifDetailsMode(identity.flow).asksSwitchOrAddition && (
          <WorkflowPifSelectField
            {...field}
            fieldKey={F.switchOrAddition}
            label="Position switch or addition?"
            options={SWITCH_OR_ADDITION}
            placeholder="Choose one…"
            required
          />
        )}
        <WorkflowPifSelectField
          {...field}
          fieldKey={F.department}
          label="Department"
          options={reference.departments}
          placeholder="Choose a department…"
          required
        />
        <WorkflowPifSelectField
          {...field}
          fieldKey={F.position}
          label="Position"
          options={reference.positions}
          placeholder={department ? "Choose a position…" : "Select a department first"}
          required
          disabled={!department}
        />
        <WorkflowPifSelectField
          {...field}
          fieldKey={F.reportsTo}
          label="Reports to"
          options={reference.reportsTo}
          placeholder="Choose a supervisor…"
          required
        />
        <LabeledField
          controlId={pifControlId(F.firstWorkingDay)}
          label="First working day"
          required
          error={errors[F.firstWorkingDay]}
          hint={isPifStartDatePast(firstWorkingDay) ? "This date has passed: add a note for the approvers below." : undefined}
        >
          <WorkflowDateField
            id={pifControlId(F.firstWorkingDay)}
            value={firstWorkingDay}
            onChange={(value) => onChange(F.firstWorkingDay, value)}
            required
            error={Boolean(errors[F.firstWorkingDay])}
          />
        </LabeledField>
        <WorkflowPifTextField
          {...field}
          fieldKey={F.hoursPerWeek}
          label="Hours per week"
          type="number"
          inputMode="decimal"
          required
        />
        <WorkflowPifSelectField
          {...field}
          fieldKey={F.employmentType}
          label="P/T or F/T"
          options={employmentTypes}
          placeholder="Choose one…"
          required
          hint={reference.fullTimeAllowed ? undefined : "This position is part-time only."}
        />
      </Box>

      {reference.communityDsp && (
        <>
          <Alert severity="info" className="workflow-run__alert--flush">
            Community DSP: choose the service and the client this position supports.
          </Alert>
          <Box className="workflow-run__grid workflow-run__grid--spaced">
            <WorkflowPifSelectField
              {...field}
              fieldKey={F.service}
              label="Service"
              options={reference.services}
              placeholder="Choose a service…"
              required
            />
            <WorkflowPifSelectField
              {...field}
              fieldKey={F.client}
              label="Client"
              options={reference.clients}
              placeholder={values[F.service] ? "Choose a client…" : "Select a service first"}
              required
              disabled={!values[F.service]}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default WorkflowPifPositionSection;
